import { PLACEHOLDER, STORE_PRODUCT } from "theme/config/constants";
import React, { useEffect, useRef, useState } from "react";
import { imgixSrc, stormSrc } from "core-web/libs/image";

import ArrowIcon from "theme/components/icons/ArrowIcon";
import Events from "core-web/libs/Events";
import Image from "core-web/components/Image";
import Link from "core-web/components/Link";
import Price from "theme/components/products/Price";
import { ProductEvents } from "core-web/libs/Events/constants";
import PropTypes from "prop-types";
import TextStyles from "theme/components/text/TextStyles";
import ThemeButton from "theme/components/buttons/ThemeButton";
import styled from "theme/libs/styled";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";
import { useSelector } from "react-redux";
import { useTheme } from "emotion-theming";
import { useTranslation } from "react-i18next";
import { checkForLocalePrefix } from "core-web/utils/checkForLocalePrefix";

const Wrapper = styled("article")`
    position: relative;
    display: grid;
    grid-template-columns: 25% auto;
    height: auto;
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    transition: ${({ theme }) => theme.transitions.primary};

    img {
        transition: scale 0.3s;
    }

    &:hover {
        img {
            scale: 1.05;
        }
    }
`;

const ContentWrapper = styled("div")`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(2, auto);
    gap: 4px 8px;

    grid-template-areas:
        "name"
        "price";

    padding: 12px;

    ${({ theme }) => theme.above.tabletLg} {
        grid-template-columns: 1fr auto;
        grid-template-rows: repeat(2, auto);
        grid-template-areas:
            "name name"
            "price button";
        padding: 16px;
    }
`;

const ImageWrapper = styled("div")`
    display: flex;
    place-items: center;
    padding: 12px;
    height: 100%;
    width: 100%;
`;

const SmallProductCard = ({
    categories,
    categoryPrimary,
    manufacturerName,

    isThreeColumn = false,

    onClick,
    partNo,
    priceExVat,
    standardPriceExVat,

    vatRate,
    productId,
    priceListCode,

    name,
    image,
    url,
    priceListId,
    pricing,
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const isMobile = useBelowBreakpoint("tabletSm");

    const ecommerceEnabled = useSelector(({ application }) => application.ecommerceEnabled);

    const { exVat, incVat } = pricing?.pricelists?.web || {};

    const useSmallerSize = isMobile || isThreeColumn;

    const wrapperRef = useRef(null);
    const wrapperHeight = wrapperRef?.current?.clientHeight;
    const [heightValue, setHeightValue] = useState(`${wrapperHeight}px`);

    // This useEffect is needed for a smooth first open-animation in SSR.
    useEffect(() => {
        setHeightValue(`${wrapperRef?.current?.clientHeight}px`);
    }, [wrapperRef]);

    const tracking = () => {
        Events.trigger(ProductEvents.CLICK, {
            product: {
                categories,
                categoryPrimary,
                manufacturerName,
                name,
                partNo,
                priceExVat,
            },
        });
    };

    const imageSrc = image
        ? stormSrc(image, { m: "pad", f: "webp", w: 80, h: 80 })
        : imgixSrc(PLACEHOLDER, { fm: "avif", fit: "fill", fill: "solid", w: 80, h: 80 });

    const placeholder = image ? stormSrc(image, { m: "pad", f: "webp", q: 10, w: 80, h: 80 }) : imageSrc;

    const handleNavigationClick = () => {
        tracking();

        if (onClick) {
            onClick();
        }
    };

    const localPrefix = checkForLocalePrefix(url);
    const productSlug = url.replace(`${localPrefix}/`, "");

    return (
        <Link to={isMobile && productSlug}>
            <Wrapper ref={wrapperRef} height={heightValue}>
                <Link to={!isMobile && productSlug} onClick={handleNavigationClick}>
                    <ImageWrapper background={colors.neutral_50}>
                        <Image
                            placeholder={placeholder}
                            alt={name}
                            title={name}
                            src={{
                                url: imageSrc,
                                width: [100, 200],
                            }}
                            mixBlendMode="multiply"
                            height="100%"
                        />
                    </ImageWrapper>
                </Link>

                <ContentWrapper>
                    <TextStyles
                        textStyles={
                            useSmallerSize
                                ? "Myriad Pro/14-140"
                                : ["Myriad Pro/14-140", null, null, null, "Myriad Pro/20-140"]
                        }
                        gridArea="name"
                    >
                        {name}
                    </TextStyles>

                    {ecommerceEnabled && (
                        <Price
                            gridArea="price"
                            priceExVat={exVat || priceExVat}
                            standardPriceExVat={standardPriceExVat}
                            vatRate={vatRate}
                            priceListCode={priceListCode}
                            priceListId={priceListId}
                            textStyles={
                                useSmallerSize
                                    ? "Myriad Pro/14-140"
                                    : ["Myriad Pro/14-140", null, null, null, "Myriad Pro/20-140"]
                            }
                            alignSelf="end"
                        />
                    )}

                    {!isMobile && (
                        <ThemeButton
                            as={Link}
                            to={productSlug}
                            size={useSmallerSize ? "xs" : ["xs", null, "lg", "sm", "lg"]}
                            gridArea="button"
                            alignSelf="end"
                        >
                            {t(ecommerceEnabled ? "product_card.shop_now" : "general.read_more")}
                            <ArrowIcon color="currentColor" direction="right" />
                        </ThemeButton>
                    )}
                </ContentWrapper>
            </Wrapper>
        </Link>
    );
};

SmallProductCard.propTypes = {
    categories: PropTypes.array.isRequired,
    categoryPrimary: PropTypes.string.isRequired,
    flags: PropTypes.array.isRequired,
    fromExVat: PropTypes.number.isRequired,
    image: PropTypes.string.isRequired,
    inStock: PropTypes.bool.isRequired,
    manufacturerName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    partNo: PropTypes.string.isRequired,
    priceExVat: PropTypes.number.isRequired,
    priceListCode: PropTypes.string,
    priceListId: PropTypes.number,
    productId: PropTypes.number.isRequired,
    standardFromExVat: PropTypes.number.isRequired,
    url: PropTypes.string.isRequired,
    vatRate: PropTypes.number.isRequired,
};

SmallProductCard.defaultProps = {
    onClick: null,
    priceListCode: null,
    priceListId: null,
};

export default SmallProductCard;
